import type { VoidFunctionComponent } from 'react';

import Redirect from '../../../generic/Redirect';
import ProjectNotificationsSettings from '../../../shared/settings/ProjectNotificationsSettings';
import Header from '../../../shared/settings/SettingsPageHeader';
import PageContent from '../shared/SettingsPageContent';
import type { NotificationUserType } from '../../../../../shared/settings/project/notifications';

import useProjectNotificationsSettings from './useProjectNotificationsSettings';

/**
 * NotificationsSettingsContainer
 */
const NotificationsSettingsContainer: VoidFunctionComponent = () => {
  const {
    users,
    settings,
    isEmailNotificationsEnabled,
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    onSave,
    onUpdate,
    onUpdateUsersToAll,
    onUpdateUsersToCurrent,
    onDisableEmailNotifications,
    onInitCustomUsers,
    canUserEditSettings,
    projectSettingsBaseURL,
  } = useProjectNotificationsSettings();

  if (!canUserEditSettings) {
    return (
      <Redirect to={projectSettingsBaseURL} />
    );
  }

  return (
    <PageContent isLoading={isLoading}>
      <Header heading="Notifications">
        <Header.SaveButton
          isLoading={isSaving}
          isDisabled={isSaveButtonDisabled}
          onClick={onSave}
        />
      </Header>
      <ProjectNotificationsSettings
        notificationDefaultEnabled={settings.notificationDefaultEnabled}
        onUpdateNotificationDefaultEnabled={(notificationDefaultEnabled: boolean) =>
          onUpdate({ notificationDefaultEnabled })}
        isEmailNotificationsEnabled={isEmailNotificationsEnabled}
        onIsEmailNotificationsEnabledChange={(isChecked: boolean) => isChecked
          ? onUpdateUsersToCurrent()
          : onDisableEmailNotifications()}
        notificationDefaultEmailScanUserType={settings.notificationDefaultEmailScanUserType}
        onSelectUsersDropdownChange={(userType: NotificationUserType) => {
          if (userType === 'current') {
            onUpdateUsersToCurrent();
          } else if (userType === 'all') {
            onUpdateUsersToAll();
          } else {
            // set custom users to the current user if custom users is selected for the first time
            onInitCustomUsers();
          }
        }}
        notificationDefaultEmailScanUsers={settings.notificationDefaultEmailScanUsers}
        onConfirmCustomUserSelection={(notificationDefaultEmailScanUsers: number[]) =>
          onUpdate({ notificationDefaultEmailScanUsers })}
        users={users || []}
        notificationDefaultSlackScan={settings.notificationDefaultSlackScan}
        onToggleSlackNotifications={(notificationDefaultSlackScan: boolean) =>
          onUpdate({ notificationDefaultSlackScan })}
        notificationDefaultApiWebhookScan={settings?.notificationDefaultApiWebhookScan || false}
        onToggleApiWebhookNotifications={(notificationDefaultApiWebhookScan: boolean) =>
          onUpdate({ notificationDefaultApiWebhookScan })}
      />
    </PageContent>
  );
};

export default NotificationsSettingsContainer;
